 /**
  * CR312: Stable JSON comparison utilities
  * 
  * Ensures consistent comparison of objects regardless of key ordering.
  * Prevents "phantom dirty" states caused by JSON.stringify key order differences.
  */
 
 /**
  * Recursively sort object keys for stable stringification
  */
 function sortObjectKeys(obj: unknown): unknown {
   if (obj === null || obj === undefined) {
     return obj;
   }
   
   if (Array.isArray(obj)) {
     return obj.map(sortObjectKeys);
   }
   
   if (typeof obj === 'object') {
     const sorted: Record<string, unknown> = {};
     const keys = Object.keys(obj as Record<string, unknown>).sort();
     for (const key of keys) {
       sorted[key] = sortObjectKeys((obj as Record<string, unknown>)[key]);
     }
     return sorted;
   }
   
   return obj;
 }
 
 /**
  * Normalize an object by:
  * 1. Sorting keys recursively
  * 2. Removing undefined values
  * 3. Converting empty objects/arrays to consistent representation
  */
 export function normalizeForComparison(obj: unknown): unknown {
   if (obj === null || obj === undefined) {
     return null;
   }
   
   if (Array.isArray(obj)) {
     return obj.map(normalizeForComparison);
   }
   
   if (typeof obj === 'object') {
     const normalized: Record<string, unknown> = {};
     const keys = Object.keys(obj as Record<string, unknown>).sort();
     for (const key of keys) {
       const value = (obj as Record<string, unknown>)[key];
       // Skip undefined values entirely
       if (value !== undefined) {
         normalized[key] = normalizeForComparison(value);
       }
     }
     return normalized;
   }
   
   return obj;
 }
 
 /**
  * Create a stable JSON string from an object
  * Keys are sorted to ensure consistent output regardless of insertion order
  */
 export function stableStringify(obj: unknown): string {
   return JSON.stringify(normalizeForComparison(obj));
 }
 
 /**
  * Compare two objects for deep equality using stable stringification
  * Returns true if the objects are equivalent (ignoring key order)
  */
 export function stableEquals(a: unknown, b: unknown): boolean {
   return stableStringify(a) === stableStringify(b);
 }
 
 /**
  * Simple hash of a stable JSON string (for debugging/logging)
  */
 export function stableHash(obj: unknown): string {
   const str = stableStringify(obj);
   let hash = 0;
   for (let i = 0; i < str.length; i++) {
     const char = str.charCodeAt(i);
     hash = ((hash << 5) - hash) + char;
     hash = hash & hash; // Convert to 32bit integer
   }
   return Math.abs(hash).toString(36);
 }